<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'

import { ProductGroupType } from '@xcel-next/core/enums'

import { useFlickitySlider, flickitySliderProps } from '../../composables/use-flickity-slider'
import { useProductGroup } from '../../composables/use-product-group'
import Product from '../shared/product/Product.vue'
import OtherPartners from '../shared/product/OtherPartners.vue'
import EditorialStarRating from '../shared/product/EditorialStarRating.vue'
import ProsAndCons from '../shared/product/ProsAndCons.vue'
import TechnicalData from '../shared/product/TechnicalData.vue'
import AffiliateInfo from '../shared/AffiliateInfo.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { UnifiedProduct } from '../../composables/use-product-group'
import type { ProductGroupParagraph } from '../../utils/paragraph-schema/paragraphs'

const props = withDefaults(defineProps<{
  productGroup: ProductGroupParagraph['productGroup'],
  brand?: Brand,
  isContentCommerce?: boolean
  sliderNo?: number,
  initialIndex?: number,
  isPreview?: boolean,
}>(), {
  isContentCommerce: false,
  sliderNo: flickitySliderProps.sliderNo.default,
  initialIndex: flickitySliderProps.initialIndex.default,
  isPreview: false
})

const rootDiv = ref<HTMLElement | null>(null)
const flickityDiv = ref<HTMLElement | null>(null)

const {
  flickity,
  sliderControlDescription,
  sliderElements,
  setAttributeForElements,
  initTabindices,
  setAriaAttributes,
  triggerResize,
  initFlickitySlider
} = useFlickitySlider(flickityDiv, { productGroup: () => props.productGroup })
const {
  unifiedProducts,
  propertyExists,
  hasManualProduct,
  labelImageUrl,
  productGroupStyleClasses,
  innerGroupStyleClasses,
  syncLabelHeights
} = useProductGroup({
  brand: () => props.brand,
  image: () => props.productGroup?.image,
  products: () => props.productGroup?.products,
  productGroupType: () => props.productGroup?.productGroupType,
  isPreview: () => props.isPreview
})

// plain variables on purpose - none of this is read from the template or watched, so it doesn't need reactivity overhead
let lastActiveIndex: string | false = false
let updatingCells = false
let intervalId: ReturnType<typeof setInterval> | false = false
let flickityOptions: Record<string, unknown> = {}
let cellOrder: string[] = []
let setSelected = 0
let preClones: HTMLElement[] = []
let postClones: HTMLElement[] = []
// tracks which cell currently has aria-hidden manually removed for keyboard focus, see focusCell()
let lastFocusedCell: HTMLElement | null = null

const isSlider = computed(() => props.productGroup.productGroupType === ProductGroupType.slider)
const isSingle = computed(() => props.productGroup?.productGroupType === ProductGroupType.single)

const flickitySliderId = computed(() => (
  isSlider.value ? `paragraph-type-product-group-flickity-${props.sliderNo}` : undefined
))

const editorialStarRating = computed(() => unifiedProducts.value[0]?.editorialStarRating)

const sliderAriaLabel = computed(() => (
  isSlider.value ? `Produktkarussell: ${unifiedProducts.value.length} Produkte. ${sliderControlDescription}` : undefined
))

// watched below to detect relevant changes to the product group data in the GUI preview
const productList = computed(() => ({
  type: props.productGroup.productGroupType,
  products: props.productGroup.products
}))

const setCellOrder = (): void => {
  cellOrder = []
  for (let i = 0; i < unifiedProducts.value.length; i++) {
    cellOrder.push(`bx-js-slider-cell-${props.sliderNo}-${i}`)
  }
}

const setSliderCellId = (product: UnifiedProduct, index: number) =>
  isSlider.value
    ? `bx-js-slider-cell-${props.sliderNo}-${index}`
    : props.productGroup.productGroupType === ProductGroupType.single ? product.link.url : false

const cloneCells = (): void => {
  if (props.isPreview) return

  cellOrder.forEach((cellId, index) => {
    const originCell = rootDiv.value?.querySelector<HTMLElement>(`#${cellId}`)
    if (!originCell) return

    const preClone = originCell.cloneNode(true) as HTMLElement
    preClone.setAttribute('id', preClone.getAttribute('id') + '-pre')
    preClone.dataset.cellIndex = String(index + unifiedProducts.value.length * 2) // set cell index to the right of original cells
    preClones[index] = preClone

    const postClone = originCell.cloneNode(true) as HTMLElement
    postClone.setAttribute('id', postClone.getAttribute('id') + '-post')
    postClone.dataset.cellIndex = String(index + unifiedProducts.value.length) // set cell index to the right of original cells
    postClones[index] = postClone

    // insert() with no index defaults to appending anyway, so use append() directly and avoid a cast for the missing index
    flickity.value?.append(originCell)
  })
  cellOrder = [...cellOrder.map(val => `${val}-pre`), ...cellOrder, ...cellOrder.map(val => `${val}-post`)]
  flickity.value?.prepend(preClones)
  flickity.value?.append(postClones)
  preClones = []
  postClones = []
  flickity.value?.select(setSelected)
  lastActiveIndex = cellOrder[setSelected]
  triggerResize()
}

const updateCellOrder = (): void => {
  const selectedCell = rootDiv.value?.querySelector(`#${flickitySliderId.value} .flickity-viewport .is-selected`)
  if (selectedCell) {
    const selectedCellId = selectedCell.getAttribute('id')
    const activeIndex = cellOrder.findIndex(id => id === selectedCellId)
    const latestIndex = cellOrder.findIndex(id => id === lastActiveIndex)
    const keyDiff = activeIndex - latestIndex
    for (let i = 0; i < Math.abs(keyDiff); i++) {
      if (keyDiff < 0) { // slide left
        const lastCell = cellOrder.pop()
        if (lastCell) {
          cellOrder.unshift(lastCell)
          const lastNode = rootDiv.value?.querySelector(`#${lastCell}`)
          if (lastNode) {
            flickity.value?.remove(lastNode)
            flickity.value?.prepend(lastNode)
          }
        }
      } else if (keyDiff > 0) { // slide right
        const firstCell = cellOrder.shift()
        if (firstCell) {
          cellOrder.push(firstCell)
          const firstNode = rootDiv.value?.querySelector(`#${firstCell}`)
          if (firstNode) {
            flickity.value?.remove(firstNode)
            flickity.value?.append(firstNode)
          }
        }
      }
    }
    lastActiveIndex = selectedCellId ?? false
  }
  if (intervalId) clearInterval(intervalId)
  updatingCells = false
  // flickity.append()/prepend() rebuild Cell wrappers, which unconditionally re-add
  // aria-hidden - undo that if it just landed on the cell the user still has focused
  const active = document.activeElement
  if (active instanceof HTMLElement && active.getAttribute('aria-hidden') === 'true' && active === lastFocusedCell) {
    active.removeAttribute('aria-hidden')
  }
}

// flickity marks every cell it doesn't consider selected as aria-hidden - since our focus
// target is picked by geometry rather than flickity's own (mismatched) selection, we have to
// un-hide it ourselves and restore aria-hidden on whichever cell we're leaving
const focusCell = (cell?: HTMLElement | null): void => {
  if (!cell) return
  if (lastFocusedCell && lastFocusedCell !== cell) {
    lastFocusedCell.setAttribute('aria-hidden', 'true')
  }
  cell.removeAttribute('aria-hidden')
  cell.focus()
  lastFocusedCell = cell
}

const exitFocusedCell = (): void => {
  lastFocusedCell?.setAttribute('aria-hidden', 'true')
  lastFocusedCell = null
}

const getCenteredCell = (): HTMLElement | undefined => {
  // the product-group slider centers cells via CSS flex/transform overrides rather than
  // flickity's own positioning, so flickity's is-selected/selectedElement doesn't reliably
  // match what's visually centered - find it by geometry instead
  const container = flickityDiv.value
  if (!container) return undefined

  const containerCenter = container.getBoundingClientRect().left + container.clientWidth / 2
  const cells = Array.from(container.querySelectorAll<HTMLElement>('.bx-product'))
  return cells.reduce<{ cell?: HTMLElement, distance: number }>((closest, cell) => {
    const rect = cell.getBoundingClientRect()
    const distance = Math.abs((rect.left + rect.width / 2) - containerCenter)
    return distance < closest.distance ? { cell, distance } : closest
  }, { cell: undefined, distance: Infinity }).cell
}

const focusSelectedCell = (): void => {
  focusCell(getCenteredCell())
}

// cells are laid out via flex in DOM order, so the next/previous centered cell is simply the
// adjacent sibling - no need to wait for flickity's transition to settle before computing it
const getAdjacentCell = (direction: 'next' | 'previous'): HTMLElement | null | undefined => {
  const centeredCell = getCenteredCell()
  if (!centeredCell) return undefined

  const sibling = (direction === 'next' ? centeredCell.nextElementSibling : centeredCell.previousElementSibling) as HTMLElement | null
  const wrapped = (direction === 'next' ? centeredCell.parentElement?.firstElementChild : centeredCell.parentElement?.lastElementChild) as HTMLElement | null
  return sibling ?? wrapped
}

// Enter (while the whole slider is focused) moves focus into the current item; arrow keys
// then move between items; Escape hands focus back to the whole slider.
const handleSliderKeydown = (event: KeyboardEvent): void => {
  if (!isSlider.value || !flickity.value) return

  if (document.activeElement === flickityDiv.value) {
    if (event.key === 'Enter') {
      event.preventDefault()
      focusSelectedCell()
    }
    return
  }

  if (event.key === 'Escape') {
    event.preventDefault()
    exitFocusedCell()
    flickityDiv.value?.focus()
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    // compute the target before next() (DOM order, instant - no need to wait for the transition),
    // but only focus it afterwards - flickity.next() toggles aria-hidden/is-selected on its own
    // (desynced) notion of the selected cell, which would otherwise clobber our fix-up
    const target = getAdjacentCell('next')
    flickity.value.next()
    focusCell(target)
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault()
    const target = getAdjacentCell('previous')
    flickity.value.previous()
    focusCell(target)
  }
}

// autoslide should stop for good once the slider (or an item inside it) receives focus
const stopAutoPlayOnFocus = (): void => {
  flickity.value?.pausePlayer()
}

watch(productList, (newData, oldData) => {
  if (props.isPreview && newData.type === ProductGroupType.slider && (newData.products?.length !== oldData.products?.length || newData.type !== oldData.type)) {
    flickity.value?.destroy()
    setCellOrder()
    nextTick(() => {
      initFlickitySlider(flickityOptions)
    })
  } else if (props.isPreview && flickity.value && newData.type !== ProductGroupType.slider) {
    flickity.value.destroy()
  }
})

onMounted(async () => {
  if (isSlider.value && unifiedProducts.value) {
    setSelected = Math.round((unifiedProducts.value.length * 3) / 2)
    flickityOptions = {
      initialIndex: setSelected,
      cellAlign: 'center',
      autoPlay: !props.isPreview, // autoplay should only be active in production, otherwise it would interfere with GUI preview
      freeScroll: false,
      groupCells: false,
      resize: false,
      on: {
        ready: () => {
          nextTick(() => {
            // create array of slider cell id's based on unified products and set cell order
            setCellOrder()
            cloneCells()
            // cells are only reachable via Enter + arrow keys, never via regular Tab flow
            setAttributeForElements(sliderElements.value.cells, 'tabindex', '-1')
            // sliderElements is a computed that only tracks flickityDiv itself, not our manual
            // DOM mutations above - its first access gets cached, so anything reading it (like
            // the two calls below) must run after cloneCells(), or the clones end up tabbable
            initTabindices()
            setAriaAttributes()
          })
          syncLabelHeights(rootDiv.value)
        },
        change: () => {
          if (!updatingCells && !props.isPreview) {
            updatingCells = true
            intervalId = setInterval(() => { updateCellOrder() }, 500)
          }
        }
      }
    }
    await initFlickitySlider(flickityOptions)
  }
})
</script>

<template>
  <div ref="rootDiv"
       :class="productGroupStyleClasses"
       data-tc="product-group">
    <figure v-if="labelImageUrl"
            class="bx-product-group__label"
            data-tc="product-group-label">
      <img :src="labelImageUrl"
           :width="productGroup.image?.width"
           :height="productGroup.image?.height"
           loading="lazy"
           :alt="productGroup.image?.imageAltText">
    </figure>
    <div v-else-if="productGroup.label"
         class="bx-product-group__label"
         data-tc="product-group-label">
      <span>
        {{ productGroup.label }}
      </span>
    </div>
    <span v-else
          class="bx-product-group__label--no" />
    <AffiliateInfo v-if="productGroup?.hideAffiliateBadge !== true"
                   :manual-product-included="hasManualProduct()"
                   :product-group-type="productGroup.productGroupType" />
    <!-- eslint-disable vue/no-unused-refs MIXIN -->
    <div :id="flickitySliderId"
         ref="flickityDiv"
         :class="innerGroupStyleClasses"
         :data-tc="`product-group-${productGroup.productGroupType}`"
         :aria-label="sliderAriaLabel"
         @keydown="handleSliderKeydown"
         @focusin="stopAutoPlayOnFocus">
      <Product v-for="(product, productIndex) in unifiedProducts"
               :id="setSliderCellId(product, productIndex)"
               :key="`product-${productIndex}`"
               :product-group-type="productGroup.productGroupType"
               :product="product"
               :product-index="productIndex"
               :data-cell-index="productIndex"
               :is-content-commerce="isContentCommerce"
               :is-preview="isPreview"
               :no-extended-information-below="productGroup.productGroupType === 'single' && product.extendedInformationOrder.length === 0"
               :reserve-space-for-label="propertyExists('label')"
               :reserve-space-for-label-shopping-days="propertyExists('shoppingDaysLabel')" />
      <OtherPartners v-if="isSingle && propertyExists('otherPartners')"
                     :unified-products="unifiedProducts" />
      <EditorialStarRating v-if="isSingle && propertyExists('editorialStarRating')"
                           title="Unsere Bewertung:"
                           size="small"
                           :hide-border="unifiedProducts[0].extendedInformationOrder.indexOf('editorialStarRating') === 0"
                           :unique-product-id="unifiedProducts[0].uniqueId"
                           :rating="editorialStarRating" />
      <ProsAndCons v-if="isSingle && propertyExists('prosAndCons')"
                   :unified-products="unifiedProducts"
                   :product-group-type="productGroup.productGroupType" />
      <TechnicalData v-if="isSingle && propertyExists('technicalData')"
                     :unified-products="unifiedProducts" />
    </div>
  </div>
</template>
